import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Info, Pencil } from "lucide-react";
import { Alert, AlertDescription, Badge, Button, CopyableId, DescriptionItem, DescriptionList } from "@fox1ck-org/ui";
import { CampaignRuleMatch, CostModel } from "@gen/tracker/v1/campaign_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { Panel } from "@/components/campaign/panel";
import { useT, type TKey } from "@/lib/i18n";
import { formatStamp } from "@/lib/utils";
import { useGetTrafficSource } from "@/lib/services/traffic-source-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { askFields, kindFromProto } from "@/lib/source-types";
import type { CardContext } from "./card";

const COST: Partial<Record<CostModel, TKey>> = {
  [CostModel.AUTO]: "setup.cost.auto",
  [CostModel.CPC]: "setup.cost.cpc",
  [CostModel.CPA]: "setup.cost.cpa",
  [CostModel.REVSHARE]: "setup.cost.revshare",
  [CostModel.MANUAL]: "setup.cost.manual",
};

/**
 * Настройки (доска CSettings): how the campaign is set now, read-only, in four
 * blocks — the source, the domain and address, the spend, the technical ids.
 * There is ONE form for a campaign, the editor (decision 05); every «Изменить»
 * here opens it at its section.
 */
export function SettingsTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const tz = useReportTimezone();
  const c = ctx.campaign;
  const source = useGetTrafficSource(c.trafficSourceId || undefined);
  const asks = source.data ? askFields(kindFromProto(source.data.kind)) : [];
  const rate =
    c.costModel === CostModel.REVSHARE
      ? `${c.defaultCost?.amount ?? "0"}%`
      : c.costModel === CostModel.CPC || c.costModel === CostModel.CPA
        ? `${c.defaultCost?.amount ?? "0"} ${c.defaultCost?.currency || "USD"}`
        : undefined;
  // The link's key: the rule that sends a visit without it elsewhere.
  const key = c.rules.find((r) => r.match === CampaignRuleMatch.PARAM_NOT_EQUAL);
  const edit = (section: string) => (
    <WriteOnly>
      <Button asChild variant="ghost" size="sm">
        <Link to={`/campaigns/${c.id}/edit#${section}`}>{t("card.action.edit")}</Link>
      </Button>
    </WriteOnly>
  );
  const mono = (v: ReactNode) => <span className="font-mono text-sm">{v}</span>;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <Alert variant="info" className="flex shrink-0 items-center gap-3">
        <Info className="size-4" />
        <AlertDescription className="flex-1">{t("card.settings.oneForm.full")}</AlertDescription>
        <WriteOnly>
          <Button asChild variant="secondary" size="sm">
            <Link to={`/campaigns/${c.id}/edit`}>
              <Pencil className="size-3.5" /> {t("card.settings.openEditor")}
            </Link>
          </Button>
        </WriteOnly>
      </Alert>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <Panel title={t("card.settings.source")} aside={edit("source")}>
          <DescriptionList className="p-3.5">
            <DescriptionItem label={t("setup.source.platform")} value={source.data?.name || ctx.item?.trafficSourceName || undefined} />
            {asks.map((a) => (
              <DescriptionItem key={a.key} label={a.label} value={c.platformParams[a.key] ? mono(c.platformParams[a.key]) : undefined} />
            ))}
            <DescriptionItem label={t("setup.group")} value={ctx.item?.groupName || t("setup.group.none")} />
            <DescriptionItem
              label={t("setup.tags")}
              value={
                c.tags.length ? (
                  <span className="flex flex-wrap gap-1">
                    {c.tags.map((x) => (
                      <Badge key={x.id || x.name}>{x.name}</Badge>
                    ))}
                  </span>
                ) : undefined
              }
            />
            <DescriptionItem label={t("setup.notes")} value={c.notes || undefined} />
          </DescriptionList>
        </Panel>
        <Panel title={t("card.settings.domain")} aside={edit("domain")}>
          <DescriptionList className="p-3.5">
            <DescriptionItem
              label={t("card.history.field.domain")}
              value={
                ctx.item?.fqdn ? (
                  <span className="flex items-center gap-2">
                    {mono(ctx.item.fqdn)}
                    <Badge variant={ctx.item.domainServes ? "ok" : "warn"}>{ctx.item.domainServes ? t("setup.domain.https") : t("setup.domain.binding")}</Badge>
                  </span>
                ) : undefined
              }
            />
            <DescriptionItem label={t("setup.domain.path")} value={c.publicPath ? mono(`/${c.publicPath}`) : undefined} />
            <DescriptionItem
              label={t("card.settings.key")}
              value={
                key ? (
                  <span>
                    {mono(`${key.paramName}=${key.paramValue}`)} <span className="text-xs text-muted-foreground">{t("card.settings.key.without")}</span>
                  </span>
                ) : undefined
              }
            />
          </DescriptionList>
        </Panel>
        <Panel title={t("card.settings.cost")} aside={edit("cost")}>
          <DescriptionList className="p-3.5">
            <DescriptionItem label={t("card.settings.cost.how")} value={COST[c.costModel] ? t(COST[c.costModel]!) : undefined} />
            <DescriptionItem label={t("setup.cost.rate")} value={rate ? mono(rate) : undefined} />
            {c.costModel === CostModel.AUTO ? (
              <DescriptionItem label={t("card.settings.cost.from")} value={t("card.settings.cost.auto", { source: ctx.item?.trafficSourceName || t("links.source") })} />
            ) : null}
          </DescriptionList>
        </Panel>
        <Panel title={t("card.settings.tech")}>
          <DescriptionList className="p-3.5">
            <DescriptionItem label={t("card.settings.id")} value={<CopyableId value={c.id} label={t("card.settings.id")} truncate={false} className="font-mono text-sm" />} />
            <DescriptionItem label={t("card.settings.token")} value={c.token ? mono(c.token) : undefined} />
            <DescriptionItem label={t("card.settings.created")} value={[formatStamp(c.createdAt, tz, { clock: true }), ctx.item?.ownerName].filter(Boolean).join(" · ") || undefined} />
            <DescriptionItem
              label={t("card.settings.version")}
              value={
                <span>
                  {mono(`r${c.revision}`)} ·{" "}
                  <Link to={ctx.href("history")} className="text-primary hover:underline">
                    {t("card.settings.history")}
                  </Link>
                </span>
              }
            />
          </DescriptionList>
        </Panel>
      </div>
    </div>
  );
}
